import { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { usePoll } from '@/hooks/usePoll';
import { PowMiningBar } from '@/components/stashstr/PowMiningBar';
import { POLL_VOTE_POW } from '@/lib/stashstr';
import { mineThenPublish } from '@/lib/publishPow';
import { cn } from '@/lib/utils';

interface PollProps {
  pollId: string;
}

export function Poll({ pollId }: PollProps) {
  const { user } = useCurrentUser();
  const { data: poll, isLoading, isError } = usePoll(pollId, user?.pubkey);

  if (isLoading) {
    return (
      <Card className="mt-3">
        <CardContent className="py-4 space-y-2">
          <div className="h-4 w-2/3 bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
          <div className="h-3 w-full bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
          <div className="h-3 w-5/6 bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !poll || poll.options.length === 0) {
    return null;
  }

  return (
    <Card className="mt-3">
      <CardContent className="py-4">
        <div className="text-[12px] text-gray-500 dark:text-gray-400 mb-1">
          poll · single choice · {poll.totalVotes} {poll.totalVotes === 1 ? 'vote' : 'votes'}
        </div>
        <PollBody poll={poll} pollId={pollId} />
      </CardContent>
    </Card>
  );
}

function PollBody({ poll, pollId }: { poll: NonNullable<ReturnType<typeof usePoll>['data']>; pollId: string }) {
  const { user } = useCurrentUser();
  const publish = useNostrPublish();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | undefined>(poll.myVote);
  const [mining, setMining] = useState(false);
  const [tries, setTries] = useState(0);
  const [miningStart, setMiningStart] = useState(0);
  const cancelRef = useRef(false);
  const [error, setError] = useState<string | undefined>();

  const vote = async () => {
    if (!user || !selected || mining) return;
    // NOTE: error text intentionally NOT cleared here — it clears only when
    // the next PoW succeeds (locked spec).
    setMining(true);
    cancelRef.current = false;
    setTries(0);
    setMiningStart(Date.now());
    try {
      await mineThenPublish({
        userPubkey: user.pubkey,
        publish: publish.mutateAsync,
        kind: 1018,
        content: '',
        // Dual-case reference tags: relays index lowercase only; uppercase
        // kept for NIP-88-style clients that read E.
        tags: [
          ['E', pollId],
          ['e', pollId],
          ['option', selected],
        ],
        difficulty: POLL_VOTE_POW,
        onProgress: setTries,
        shouldCancelRef: cancelRef,
      });
      // PoW + publish succeeded: clear previous error, refresh tally
      setError(undefined);
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'poll', pollId] });
    } catch {
      setError('Vote failed or was cancelled.');
    } finally {
      setMining(false);
      setTries(0);
    }
  };

  return (
    <div className="space-y-2">
      {poll.options.map((option) => {
        const count = poll.tally.get(option.id) ?? 0;
        const pct = poll.totalVotes > 0 ? Math.round((count / poll.totalVotes) * 100) : 0;
        const isMyVote = poll.myVote === option.id;
        return (
          <div key={option.id}>
            <label
              className={cn(
                'flex items-center gap-2 text-[13px] px-2 py-1.5 rounded border',
                isMyVote
                  ? 'border-[#f97316] bg-orange-50 dark:border-[#fb923c] dark:bg-orange-950/40'
                  : 'border-gray-200 dark:border-[#2a2d30]',
              )}
            >
              <input
                type="radio"
                name={`poll-${pollId}`}
                checked={selected === option.id}
                onChange={() => setSelected(option.id)}
                disabled={mining}
                aria-label={option.label}
              />
              <span className="flex-1">{option.label}</span>
              <span className="text-gray-500 dark:text-gray-400 text-[12px]">
                {count} ({pct}%)
              </span>
            </label>
            <div className="h-1 bg-gray-100 dark:bg-[#232528] rounded mt-0.5" aria-hidden="true">
              <div
                className="h-1 bg-[#f97316] dark:bg-[#fb923c] rounded transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}

      {poll.hiddenVotes > 0 && (
        <div className="text-[11px] text-gray-400 dark:text-gray-500">
          + {poll.hiddenVotes} unproven votes hidden
        </div>
      )}

      {error && <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p>}

      {user ? (
        mining ? (
          <PowMiningBar
            tries={tries}
            difficulty={POLL_VOTE_POW}
            startedAt={miningStart}
            onCancel={() => {
              cancelRef.current = true;
            }}
          />
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={vote}
            disabled={!selected || publish.isPending}
            className="h-7 px-3 text-[12px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
          >
            {publish.isPending ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
            {poll.hasVoted ? 'Revote' : 'Mine + Vote'} (18-bit)
          </Button>
        )
      ) : (
        <p className="text-[12px] text-gray-500 dark:text-gray-400">Log in to vote.</p>
      )}
    </div>
  );
}
